import React from "react";
import { Search, CheckCircle, AlertCircle, Eye, ChevronLeft, ChevronRight } from "lucide-react";

const RequestHistory = ({
  logs = [],
  searchTerm = "",
  setSearchTerm,
  statusFilter = "all",
  setStatusFilter,
  endpointFilter = "all",
  setEndpointFilter,
  handleSearchAndFilter,
  handleViewLogDetails,
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  itemsPerPage = 10,
  onPageChange,
  loading = false,
}) => {
  const effectiveTotalCount = totalCount > 0 ? totalCount : logs.length;
  const effectiveTotalPages = Math.max(
    totalPages,
    Math.ceil(effectiveTotalCount / itemsPerPage) || 1
  );
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + logs.length, effectiveTotalCount);

  const getPageNumbers = (current, total) => {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    if (current <= 4) {
      return [1, 2, 3, 4, 5, "...", total];
    }
    if (current >= total - 3) {
      return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
    }
    return [1, "...", current - 1, current, current + 1, "...", total];
  };

  return (
    <div className="space-y-6">
      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by endpoint, method, or user agent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && handleSearchAndFilter) {
                  handleSearchAndFilter();
                }
              }}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="success">Success</option>
          <option value="error">Error</option>
        </select>
        <button
          onClick={handleSearchAndFilter}
          className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
        >
          <Search className="w-4 h-4" />
          Search
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-sm text-gray-600">
        <span>
          Showing {effectiveTotalCount > 0 ? startIndex + 1 : 0} to{" "}
          {endIndex} of {effectiveTotalCount} API Calls
        </span>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Success</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <span>Error</span>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Endpoint
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Response Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tokens
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Language Pair
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Input Preview
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-sm text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                      <span>Loading logs...</span>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-sm text-gray-500">
                    No logs found matching your request.
                  </td>
                </tr>
              ) : (
                logs.map((log, index) => {
                  const actionParts = (log.action || '').split(' • ');
                  const isSuccess = (log.status || '').toLowerCase() === 'success';
                  const tokens = log.token ?? log.tokensUsed ?? 0;
                  const endpointDisplay = log.endpoint || log.service || log.action || '-';
                  const timeDisplay = log.createdAt
                    ? (!isNaN(new Date(log.createdAt).getTime())
                        ? new Date(log.createdAt).toLocaleString()
                        : log.createdAt)
                    : log.date
                    ? (!isNaN(new Date(log.date).getTime())
                        ? new Date(log.date).toLocaleString()
                        : log.date)
                    : log.when || "-";

                  let langDisplay = "-";
                  if (log.sourceLang && log.targetLang) {
                    langDisplay = `${log.sourceLang} → ${log.targetLang}`;
                  } else if (actionParts[0] && actionParts[0].includes(' to ')) {
                    const parts = actionParts[0].split(/\s+to\s+/i);
                    if (parts.length === 2) {
                      langDisplay = `${parts[0].trim()} → ${parts[1].trim()}`;
                    }
                  }

                  const rawInput = log.input || actionParts[1] || actionParts[0] || "-";
                  const inputDisplay = rawInput.length > 30 ? `${rawInput.substring(0, 30)}...` : rawInput;

                  return (
                    <tr
                      key={log.id || log._id || index}
                      onClick={() => handleViewLogDetails(log.id || log._id)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" title={log.createdAt || timeDisplay}>
                        {timeDisplay}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                        {endpointDisplay}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                        POST
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          {isSuccess ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-red-500" />
                          )}
                          <span
                            className={`font-medium ${isSuccess ? "text-green-600" : "text-red-600"
                              }`}
                          >
                            {log.status ? log.status.toUpperCase() : (isSuccess ? "SUCCESS" : "ERROR")}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.responseTime ? `${log.responseTime}ms` : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {tokens.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {langDisplay}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                        {inputDisplay}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewLogDetails(log.id || log._id);
                          }}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {effectiveTotalCount > 0 && (
          <div className="px-6 py-4 bg-white border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs sm:text-sm text-gray-500">
              Showing <span className="font-semibold text-gray-800">{startIndex + 1}</span> to{" "}
              <span className="font-semibold text-gray-800">{endIndex}</span> of{" "}
              <span className="font-semibold text-gray-800">{effectiveTotalCount}</span> results
            </p>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => onPageChange && onPageChange(currentPage - 1)}
                disabled={currentPage <= 1 || loading}
                className="px-3 py-1.5 text-xs sm:text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>

              {getPageNumbers(currentPage, effectiveTotalPages).map((pageNum, idx) =>
                pageNum === "..." ? (
                  <span key={`dots-${idx}`} className="px-2 py-1 text-xs sm:text-sm text-gray-400 select-none">
                    ...
                  </span>
                ) : (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange && onPageChange(pageNum)}
                    disabled={loading}
                    className={`px-3 py-1.5 text-xs sm:text-sm font-bold rounded-lg transition-colors ${currentPage === pageNum
                        ? "bg-secondary text-white"
                        : "text-gray-600 hover:bg-gray-100 border border-gray-200"
                      }`}
                  >
                    {pageNum}
                  </button>
                )
              )}

              <button
                onClick={() => onPageChange && onPageChange(currentPage + 1)}
                disabled={currentPage >= effectiveTotalPages || loading}
                className="px-3 py-1.5 text-xs sm:text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestHistory;
